import React, { useEffect, useMemo, useState } from 'react';
import { Phone, MessageSquare, StickyNote, Mail, Users, FileText, RefreshCw, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { fmtDateTime, OFFICE_TZ } from '../lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  call: { Icon: Phone, label: 'Call', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  sms: { Icon: MessageSquare, label: 'Text', color: 'text-sky-700 bg-sky-50 border-sky-200' },
  whatsapp: { Icon: MessageSquare, label: 'WhatsApp', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  email: { Icon: Mail, label: 'Email', color: 'text-violet-700 bg-violet-50 border-violet-200' },
  meeting: { Icon: Users, label: 'Meeting', color: 'text-amber-700 bg-amber-50 border-amber-200' },
  note: { Icon: StickyNote, label: 'Note', color: 'text-violet-700 bg-violet-50 border-violet-200' },
};

export default function LeadActivityFeed() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ by_type: {}, by_actor: {}, by_outcome: {} });
  const [counsellors, setCounsellors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ actor_id: '', type: '', outcome: '', since: '', until: '' });

  useEffect(() => {
    fetch(`${API}/api/admin/counsellors`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : { counsellors: [] })
      .then((d) => setCounsellors(d.counsellors || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.actor_id, filters.type, filters.outcome, filters.since, filters.until]);

  async function fetchFeed() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('limit', '500');
      const r = await fetch(`${API}/api/admin/lead-activities?${params.toString()}`, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to load activities');
      const d = await r.json();
      setItems(d.activities || []);
      setSummary(d.summary || { by_type: {}, by_actor: {}, by_outcome: {} });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  const totalThisWeek = useMemo(() => {
    const weekAgo = Date.now() - 7 * 86400 * 1000;
    return items.filter((a) => new Date(a.created_at).getTime() >= weekAgo).length;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="lead-activity-feed">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard label="Total activities" value={items.length} />
        <SummaryCard label="Last 7 days" value={totalThisWeek} accent="text-emerald-600" />
        <SummaryCard label="Calls" value={summary.by_type?.call || 0} accent="text-emerald-600" />
        <SummaryCard label="Texts" value={(summary.by_type?.sms || 0) + (summary.by_type?.whatsapp || 0)} accent="text-sky-600" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-tge-navy/5 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-3 text-tge-navy/70 text-xs uppercase tracking-wider font-semibold">
          <Filter size={13} /> Filters
          <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" onClick={fetchFeed} data-testid="feed-refresh">
            <RefreshCw size={12} className="mr-1" /> Refresh
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-[11px] text-tge-muted">Team member</Label>
            <Select value={filters.actor_id || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, actor_id: v === 'all' ? '' : v }))}>
              <SelectTrigger className="mt-1" data-testid="filter-actor"><SelectValue placeholder="Anyone" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Anyone</SelectItem>
                {counsellors.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.role})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-tge-muted">Type</Label>
            <Select value={filters.type || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, type: v === 'all' ? '' : v }))}>
              <SelectTrigger className="mt-1" data-testid="filter-type"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="call">Call</SelectItem>
                <SelectItem value="sms">Text/SMS</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="note">Note</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-tge-muted">Outcome</Label>
            <Select value={filters.outcome || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, outcome: v === 'all' ? '' : v }))}>
              <SelectTrigger className="mt-1" data-testid="filter-outcome"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                <SelectItem value="reached">Reached</SelectItem>
                <SelectItem value="no_answer">No answer</SelectItem>
                <SelectItem value="voicemail">Voicemail</SelectItem>
                <SelectItem value="callback_requested">Callback requested</SelectItem>
                <SelectItem value="interested">Interested</SelectItem>
                <SelectItem value="not_interested">Not interested</SelectItem>
                <SelectItem value="qualified">Qualified</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-tge-muted">From</Label>
            <Input type="date" value={filters.since} onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value }))} className="mt-1" data-testid="filter-from" />
          </div>
          <div>
            <Label className="text-[11px] text-tge-muted">To</Label>
            <Input type="date" value={filters.until} onChange={(e) => setFilters((f) => ({ ...f, until: e.target.value }))} className="mt-1" data-testid="filter-to" />
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      {Object.keys(summary.by_actor || {}).length > 0 && (
        <div className="bg-white border border-tge-navy/5 rounded-2xl p-5">
          <h3 className="font-serif-display text-lg text-tge-navy mb-3">Team activity leaderboard</h3>
          <div className="space-y-2">
            {Object.entries(summary.by_actor).map(([name, count]) => {
              const max = Math.max(...Object.values(summary.by_actor));
              const pct = max > 0 ? (count / max) * 100 : 0;
              return (
                <div key={name} className="flex items-center gap-3">
                  <div className="w-40 text-sm text-tge-navy font-medium truncate">{name}</div>
                  <div className="flex-1 h-2 bg-tge-cream-soft rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-tge-navy to-blue-700" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-12 text-right text-xs font-mono text-tge-navy">{count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Feed */}
      <div className="bg-white border border-tge-navy/5 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-tge-cream flex items-center justify-between">
          <h3 className="font-serif-display text-lg text-tge-navy">Activity feed</h3>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-tge-muted hidden sm:inline">All times in Vancouver (PST/PDT)</span>
            <span className="text-xs text-tge-muted">{items.length} entries</span>
          </div>
        </div>
        {loading ? (
          <div className="text-center py-12 text-sm text-tge-muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-sm text-tge-muted">
            <FileText size={32} className="mx-auto mb-2 opacity-40" />
            No activities match your filters yet.
          </div>
        ) : (
          <div className="divide-y divide-tge-cream max-h-[600px] overflow-y-auto">
            {items.map((a) => {
              const meta = TYPE_META[a.type] || { Icon: StickyNote, label: a.type, color: 'text-gray-700 bg-gray-50 border-gray-200' };
              return (
                <div key={a.id} className="px-5 py-3 hover:bg-tge-cream-soft/50 transition" data-testid={`feed-entry-${a.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-full border flex items-center justify-center ${meta.color} shrink-0`}>
                      <meta.Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="font-semibold text-sm text-tge-navy">{a.actor_name}</span>
                        <span className="text-xs text-tge-muted">logged a {meta.label.toLowerCase()}</span>
                        <span className="text-xs text-tge-muted">·</span>
                        <span className="text-sm text-tge-navy/90">{a.lead_name || a.lead_phone || 'Unnamed lead'}</span>
                        {a.outcome && (
                          <span className="text-[10px] uppercase tracking-wider bg-tge-cream-soft text-tge-navy/80 px-1.5 py-0.5 rounded-full">
                            {a.outcome.replaceAll('_', ' ')}
                          </span>
                        )}
                      </div>
                      {a.note && <div className="text-xs text-tge-navy/75 mt-1 bg-tge-cream-soft/60 rounded px-2 py-1.5">{a.note}</div>}
                      <div className="text-[10px] text-tge-muted mt-1">
                        {fmtDateTime(a.created_at)} <span title={OFFICE_TZ}>· {a.actor_role}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent = 'text-tge-navy' }) {
  return (
    <div className="bg-white rounded-2xl border border-tge-navy/5 p-5">
      <div className="text-[11px] uppercase tracking-wider text-tge-muted font-semibold">{label}</div>
      <div className={`font-serif-display text-3xl mt-2 ${accent}`}>{value}</div>
    </div>
  );
}
